// NomLoop client — thin fetch wrapper around the backend API.
//
// Why this file exists:
//   - All proprietary logic (pack rounding, scoring, substitution graph, cook steps)
//     lives on the server behind /api/*. The client must NEVER implement those rules.
//   - Every network edge the client uses passes through this module so we have one
//     place to add retries, auth headers, error toasts, request logging, rate-limit
//     back-off, and a session token when PR 5.5 lands.
//
// Usage (from NomLoop.jsx):
//   import * as api from "./api.js";
//   const meals = await api.listMeals({ diet, allergens, cuisines });
//   const list  = await api.buildShoppingList({ mealIds, servings, retailerId });
//
// What NEVER goes through here:
//   - Pack-size math. Retail price math. Meal scoring.
//     These are server-only. If you find yourself wanting to do this on the client,
//     stop — it's a leak.

function isLocalHostName(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

const DEFAULT_BASE =
  typeof window !== "undefined" && isLocalHostName(window.location.hostname)
    ? "http://localhost:3001"
    : "https://nomloop-api.onrender.com";

// Allow override at runtime via <script>window.NOMLOOP_API_BASE = "..."</script>
const API_BASE =
  (typeof window !== "undefined" && window.NOMLOOP_API_BASE) || DEFAULT_BASE;

// ── JWT (auth) ───────────────────────────────────────────────────────────────
// Persisted in localStorage so sign-in survives a page refresh.
// On app boot, NomLoop.jsx calls api.consumeJwtFromUrl() to capture the token
// the server drops in the URL fragment after magic-link verification.
const JWT_KEY = "nomloop_jwt";

export function getJwt() {
  try { return localStorage.getItem(JWT_KEY); } catch { return null; }
}

function setJwt(token) {
  try { localStorage.setItem(JWT_KEY, token); } catch {}
}

export function clearJwt() {
  try { localStorage.removeItem(JWT_KEY); } catch {}
}

/**
 * Call once on app mount. Reads #jwt=<token> from the URL fragment,
 * stores it, and cleans the fragment from history so the token isn't
 * bookmarked or shared accidentally.
 * @returns {boolean} true if a new JWT was found and stored
 */
export function consumeJwtFromUrl() {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash;
  const match = hash.match(/[#&]jwt=([^&]+)/);
  if (!match) return false;
  setJwt(decodeURIComponent(match[1]));
  // Remove the fragment without triggering a page reload.
  history.replaceState(null, "", window.location.pathname + window.location.search);
  return true;
}

export function getSetPasswordTokenFromUrl() {
  if (typeof window === "undefined") return null;
  const match = window.location.hash.match(/[#&]token=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export function consumeAuthErrorFromUrl() {
  if (typeof window === "undefined") return null;
  const match = window.location.hash.match(/[#&]auth_error=([^&]+)/);
  if (!match) return null;
  const value = decodeURIComponent(match[1]);
  history.replaceState(null, "", window.location.pathname + window.location.search);
  return value;
}

// ── Session token ────────────────────────────────────────────────────────────
// PR 5.5: the server soft-gates /api/* behind a session token. We call
// /api/session/start lazily on the first /api/* request and cache the token in
// memory (no localStorage — tokens should not outlive a tab).
let _sessionToken = null;
let _sessionPromise = null;

async function getSessionToken() {
  if (_sessionToken) return _sessionToken;
  if (_sessionPromise) return _sessionPromise;
  _sessionPromise = fetch(`${API_BASE}/api/session/start`, { method: "POST" })
    .then(async r => {
      if (!r.ok) throw new Error(`session start failed: ${r.status}`);
      const { token } = await r.json();
      _sessionToken = token;
      return token;
    })
    .finally(() => { _sessionPromise = null; });
  return _sessionPromise;
}

// Exposed for tests / explicit resets; no-op otherwise.
export function _resetSessionForTests() {
  _sessionToken   = null;
  _sessionPromise = null;
}

// ── JSON-safe payload guard ───────────────────────────────────────────────────
// Defends every API call against accidentally serialising a React SyntheticEvent,
// DOM node (HTMLButtonElement), AbortController, function, or React Fiber. Such a
// value makes JSON.stringify throw "Converting circular structure to JSON", which
// historically surfaced as a raw crash on "Save to my recipes". We serialise once,
// catch the failure, name the offending top-level keys for developers, and throw a
// clean Error the UI can show as a friendly message — never the raw circular dump.
function serializeBody(body, path) {
  try {
    return JSON.stringify(body);
  } catch (err) {
    const offenders = [];
    if (body && typeof body === "object") {
      for (const [key, val] of Object.entries(body)) {
        try { JSON.stringify(val); } catch { offenders.push(key); }
      }
    }
    // eslint-disable-next-line no-console
    console.error(
      `[api] Non-serializable value reached request("${path}"). ` +
      `Offending field(s): ${offenders.join(", ") || "<root>"}. ` +
      `A DOM node, event, or function was passed where plain data was expected.`,
      err,
    );
    const e = new Error("Couldn't save — please try again.");
    e.code = "NON_SERIALIZABLE_PAYLOAD";
    throw e;
  }
}

// ── Low-level fetch helper ───────────────────────────────────────────────────
async function request(path, { method = "GET", body, signal } = {}) {
  const headers = {};
  if (body) headers["Content-Type"] = "application/json";
  // Serialise once, up front, so a non-serializable payload fails cleanly here
  // instead of crashing inside fetch with a raw circular-JSON error.
  const serializedBody = body ? serializeBody(body, path) : undefined;

  // Auth routes (/api/auth/*) are public with respect to the soft session gate,
  // but /api/auth/session and /api/auth/beta/claim still use Authorization.
  const skipsSession = path === "/api/session/start" || path.startsWith("/api/auth/");

  if (!skipsSession && path.startsWith("/api/")) {
    // Attach session token (soft gate) for all non-auth API calls.
    headers["X-NomLoop-Session"] = await getSessionToken();
  }

  // Attach JWT for authenticated routes (/api/profile, future /api/household etc.)
  const jwt = getJwt();
  if (jwt && path !== "/api/session/start") {
    headers["Authorization"] = `Bearer ${jwt}`;
  }

  let res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body:    serializedBody,
    signal,
  });

  // If the server rejects the token (expired, rotated secret, etc.) refresh
  // once and retry. One retry only — avoids a loop when the server is broken.
  if (res.status === 401 && path !== "/api/session/start") {
    _sessionToken = null;
    const token = await getSessionToken();
    headers["X-NomLoop-Session"] = token;
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: serializedBody,
      signal,
    });
  }

  if (!res.ok) {
    let detail = "";
    let code = "";
    try {
      const data = await res.json();
      detail = data.error || (Array.isArray(data.errors) ? data.errors.join("; ") : "");
      code = data.code || "";
    } catch {}
    const err = new Error(`API ${res.status} ${path}${detail ? ` — ${detail}` : ""}`);
    err.status = res.status;
    err.code = code;
    if (res.status === 429) {
      const ra = res.headers.get("Retry-After");
      if (ra) err.retryAfter = ra;
    }
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Public endpoints ─────────────────────────────────────────────────────────

// Plan endpoints — filter and score meals server-side.
// Request/response shape must mirror server/routes/plan.js exactly.
export function generatePlan({ cuisines, diet, allergens = [], dislikes = [], selectedMealIds = [], selectedIngredientIds = [], signal } = {}) {
  return request("/api/plan/generate", {
    method: "POST",
    body:   { cuisines, diet, allergens, dislikes, selectedMealIds, selectedIngredientIds },
    signal,
  });
}

// Full meal detail (includes ingredients). Used by the detail modal only.
export function getMeal(id, { signal } = {}) {
  return request(`/api/meals/${encodeURIComponent(id)}`, { signal });
}

export function autofillPlan({
  selectedMealIds = [], cuisines, diet, allergens = [], dislikes = [],
  lovedMealIds = [], skipMealIds = [], targetCount = 7, selectedIngredientIds = [],
  retailerId = "tesco", signal,
} = {}) {
  return request("/api/plan/autofill", {
    method: "POST",
    body:   { selectedMealIds, cuisines, diet, allergens, dislikes, lovedMealIds, skipMealIds, targetCount, selectedIngredientIds, retailerId },
    signal,
  });
}

// Shopping-list endpoint — the private engine (pack rounding, scoring, swaps).
export function buildShoppingList({ mealIds, servings, retailerId, organicMode = false, diet, allergens, dislikes, importedRecipes = [], appliedSwaps = [], signal } = {}) {
  const swaps = appliedSwaps.map(({ mealId, fromId, toId }) => ({ mealId, fromId, toId }));
  return request("/api/shopping-list/build", {
    method: "POST",
    body:   { mealIds, servings, retailerId, organicMode, diet, allergens: allergens || [], dislikes: dislikes || [], importedRecipes, appliedSwaps: swaps },
    signal,
  });
}

// Record an ingredient swap action (accept / dismiss / undo). Fire-and-forget.
export function swapEvent({ action, mealId, fromId, toId } = {}) {
  return request("/api/shopping-list/swap-event", {
    method: "POST",
    body:   { action, mealId, fromId, toId },
  });
}

// Fuzzy-match imported recipe ingredients against PKG catalog.
// Returns { matched, suggestions, manualItems } — pack calculation done server-side.
export function matchImportedIngredients({ ingredients, retailerId, signal } = {}) {
  return request("/api/shopping-list/match-imported", {
    method: "POST",
    body:   { ingredients, retailerId },
    signal,
  });
}

// Retailer catalogue — budget-gated list of {tier, retailers[]}.
export function listRetailers({ budget, signal } = {}) {
  return request(`/api/retailers/available?budget=${encodeURIComponent(budget)}`, { signal });
}

// Cook-guide endpoint — returns 6 display-ready steps for a single meal.
// `servings` is the target serving count (not a factor). `shoppingList` is
// optional; when supplied the server honours threshold rounded-down flags so
// gram amounts in step 1 always sum to what was actually bought.
export function getCookingSteps({ mealId, servings, shoppingList, appliedSwaps = [], signal } = {}) {
  const swaps = appliedSwaps.map(({ mealId: mid, fromId, toId }) => ({ mealId: mid, fromId, toId }));
  return request("/api/cook-guide", {
    method: "POST",
    body:   { mealId, servings, shoppingList, appliedSwaps: swaps },
    signal,
  });
}

// Cuisine metadata. Both endpoints take a single `first` cuisine id and
// return editorial copy: drink pairings (3 per cuisine) and pair labels
// (what to call every other cuisine when the user has already picked one).
// The scoring behind both stays server-side.
export function getCuisineDrinkPairings({ first, signal } = {}) {
  return request(`/api/cuisines/drink-pairings?first=${encodeURIComponent(first)}`, { signal });
}

export function getCuisinePairHints({ first, signal } = {}) {
  return request(`/api/cuisines/pair-hints?first=${encodeURIComponent(first)}`, { signal });
}

// Health check — used on app boot to decide between live API and offline message.
export function health({ signal } = {}) {
  return request("/health", { signal });
}

// ── Auth ─────────────────────────────────────────────────────────────────────

/**
 * Send a magic-link to the given email address.
 * Always resolves (server never reveals whether the email is registered).
 */
export function sendMagicLink(email, inviteCode) {
  return request("/api/auth/magic/send", {
    method: "POST",
    body:   { email, ...(inviteCode ? { inviteCode } : {}) },
  });
}

export function getAuthConfig({ signal } = {}) {
  return request("/api/auth/config", { signal });
}

export function getAuthSession({ signal } = {}) {
  return request("/api/auth/session", { signal });
}

export function startOAuth(provider) {
  if (typeof window === "undefined") return;
  const url = `${API_BASE}/api/auth/oauth/${encodeURIComponent(provider)}/start?returnTo=/app`;
  window.location.assign(url);
}

export async function signup(email, password, inviteCode) {
  const result = await request("/api/auth/signup", {
    method: "POST",
    body:   { email, password, ...(inviteCode ? { inviteCode } : {}) },
  });
  if (result?.jwt) setJwt(result.jwt);
  return result;
}

export async function login(email, password) {
  const result = await request("/api/auth/login", {
    method: "POST",
    body:   { email, password },
  });
  if (result?.jwt) setJwt(result.jwt);
  return result;
}

export async function claimBetaCode(inviteCode) {
  const result = await request("/api/auth/beta/claim", {
    method: "POST",
    body:   { inviteCode },
  });
  if (result?.jwt) setJwt(result.jwt);
  return result;
}

export async function setPassword(token, password) {
  const result = await request("/api/auth/set-password", {
    method: "POST",
    body:   { token, password },
  });
  if (result?.jwt) setJwt(result.jwt);
  return result;
}

// ── Profile ───────────────────────────────────────────────────────────────────

/**
 * Navigate the browser to the affiliate click redirect for a shopping-list item.
 * The server logs the click and 302s to the Awin deep-link (or direct URL in dev).
 *
 * Call this instead of window.open(productUrl) so every outbound click is tracked.
 *
 * @param {string} retailer   — retailer ID, e.g. "waitrose"
 * @param {string} sku        — item SKU from the shopping list response
 * @param {string} productUrl — the retailer's product page URL (from Awin feed)
 */
export function trackClick(retailer, sku, productUrl) {
  const encoded = encodeURIComponent(productUrl);
  const href    = `${API_BASE}/api/click/${encodeURIComponent(retailer)}/${encodeURIComponent(sku)}?url=${encoded}`;
  // Use location.href so the redirect replaces the current tab history entry,
  // consistent with how affiliate links are expected to work on mobile.
  window.open(href, "_blank", "noopener,noreferrer");
}

// ── Inbound signals ───────────────────────────────────────────────────────────

export function submitInbound(data) {
  return request("/api/inbound", { method: "POST", body: data });
}

// ── Wine affiliate (council 28-Apr-2026) ─────────────────────────────────────

/**
 * Fetch wine merchants editorially matched to a cuisine.
 * Returns { merchants: [{ id, name, style, clickUrl }] } — merchants array
 * is empty for cuisines without a strong wine pairing tradition (e.g. korean,
 * thai variants).
 */
export function getWinePairings({ cuisine, signal } = {}) {
  return request(`/api/wine/pairings?cuisine=${encodeURIComponent(cuisine)}`, { signal });
}

/**
 * Open the wine merchant in a new tab, server-side affiliate-wrapped.
 * @param {string} merchantId — e.g. "naked_wines", "laithwaites"
 * @param {string} cuisine    — cuisine ID for click attribution
 */
export function trackWineClick(merchantId, cuisine) {
  const href = `${API_BASE}/api/wine/click/${encodeURIComponent(merchantId)}?cuisine=${encodeURIComponent(cuisine)}`;
  window.open(href, "_blank", "noopener,noreferrer");
}

// ── Weekly plan ───────────────────────────────────────────────────────────────

/**
 * Persist the current week's meal plan server-side.
 * Required for Sunday email and cross-device sync.
 * Silently no-ops (returns null) if the user is not signed in.
 *
 * @param {{ mealIds: string[], dayMap: object, retailerId: string, people: number, totalCost?: number, checkedOut?: boolean, roundDownChoices?: object }} plan
 */
export async function savePlan({ mealIds, dayMap, retailerId, people, totalCost, checkedOut, roundDownChoices } = {}) {
  if (!getJwt()) return null; // not signed in — fail silently
  return request("/api/plan/save", {
    method: "POST",
    body:   { mealIds, dayMap, retailerId, people, totalCost, checkedOut, roundDownChoices },
  });
}

/** Fetch the saved plan for the current week. Returns { plan } or { plan: null }. */
export function getSavedPlan({ signal } = {}) {
  return request("/api/plan/saved", { signal });
}

/** Fetch the authenticated user's profile. Throws if not signed in. */
export function getProfile({ signal } = {}) {
  return request("/api/profile", { signal });
}

/**
 * Save / update user preferences. Partial updates are fine — only supplied
 * fields are overwritten.
 * @param {{ people?, diet?, cuisines?, allergens?, dislikes? }} prefs
 */
export function saveProfile(prefs) {
  return request("/api/profile", { method: "PUT", body: prefs });
}

// ── Recipe import ─────────────────────────────────────────────────────────────

/**
 * Parse a URL or text into a recipe draft. Does NOT save.
 * @param {{ type: 'url'|'text', payload: string }} params
 * @param {{ signal?: AbortSignal }} opts
 */
export function parseImport({ type, payload }, { signal } = {}) {
  return request("/api/imports/parse", { method: "POST", body: { type, payload }, signal });
}

/**
 * Save a confirmed recipe draft.
 * @param {{ title, servings, ingredients, sourceDomain, importMethod, corrections? }} draft
 * @param {{ saveAsCopy?: boolean }} opts
 *   saveAsCopy: true → bypass duplicate check and insert a new copy (copy_seq > 0)
 */
export function saveImport(draft, { saveAsCopy = false } = {}) {
  return request("/api/imports/save", { method: "POST", body: { ...draft, saveAsCopy } });
}

/** List saved imported recipes for the current user. */
export function listRecipeLibrary({ signal } = {}) {
  return request("/api/imports/library", { signal });
}

/** Delete a saved recipe by id. */
export function deleteRecipe(id) {
  return request(`/api/imports/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Fetch a single saved recipe with full ingredients. */
export function getRecipe(id) {
  return request(`/api/imports/${encodeURIComponent(id)}`);
}

// ── Pinned ingredient swaps ───────────────────────────────────────────────────

/** Fetch all pinned swaps for the current user. Returns [{ origName, pkgId }]. */
export function getSwaps({ signal } = {}) {
  return request("/api/swaps", { signal });
}

/**
 * Pin (or re-pin) an ingredient swap. Requires JWT.
 * @param {{ origName: string, pkgId: string }} swap
 */
export function pinSwap({ origName, pkgId }) {
  return request("/api/swaps", { method: "POST", body: { origName, pkgId } });
}

/**
 * Remove a pinned swap. Idempotent. Requires JWT.
 * @param {string} origName
 */
export function deletePinnedSwap(origName) {
  return request(`/api/swaps/${encodeURIComponent(origName)}`, { method: "DELETE" });
}

// ── Pantry ────────────────────────────────────────────────────────────────────

/** Fetch all pantry items. Returns { items: [{ itemKey, ownedAt }] }. */
export function getPantry({ signal } = {}) {
  return request("/api/pantry", { signal });
}

/**
 * Add an item to the pantry. Requires JWT.
 * @param {string} itemKey — "pkg:chicken_thighs" or "manual:sea salt"
 */
export function addPantryItem(itemKey) {
  return request("/api/pantry", { method: "POST", body: { itemKey } });
}

/**
 * Remove an item from the pantry. Idempotent. Requires JWT.
 * @param {string} itemKey
 */
export function removePantryItem(itemKey) {
  return request("/api/pantry", { method: "DELETE", body: { itemKey } });
}

export { API_BASE };
